import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { cn, Dialog, DialogContent, DialogTitle, DialogClose, Button } from '@scio3/ui';

type Props = {
  open: boolean;
  /** Dismiss (keep current plan / stay in team). */
  onDismiss: () => void;
  /** Switch the active context back to the Default Team. */
  onSwitchToDefault: () => void;
};

/**
 * #1275 — Free and Standard plans do NOT support Teams (Teams require Pro Plus+,
 * gated by `company.feature.allowTeams`). Downgrading to a no-Team plan while the
 * Owner is switched into a non-default team leaves the account in an invalid state —
 * the plan no longer has Teams, yet the Owner is "standing inside" a team they can no
 * longer leave (trapped). We block that downgrade up-front and steer the user back to
 * the Default Team first. scio1 has the same bug; this is the defined-correct behavior.
 */
export function TeamPlanBlockDialog({ open, onDismiss, onSwitchToDefault }: Props) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onDismiss();
      }}
    >
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
        hideCloseButton
        data-testid="team-plan-block-dialog"
      >
        <div className="flex items-center justify-between bg-amber-500 px-5 py-3 text-white">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-white">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {t('Switch to Default Team first')}
          </DialogTitle>
          <DialogClose
            className={cn(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
              'text-white/80 transition-colors',
              'hover:bg-white/15 hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
            )}
            aria-label={t('Close')}
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4">
          <p className="text-sm">
            {t(
              'The Free and Standard plans do not support Teams. Changing to one of these plans while you are inside a team would leave you unable to switch back.',
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('Switch to your Default Team first, then change your plan from there.')}
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <Button
            data-testid="team-plan-block-dismiss-btn"
            variant="outline"
            onClick={onDismiss}
            className="cursor-pointer"
          >
            {t('Cancel')}
          </Button>
          <Button
            data-testid="team-plan-block-switch-btn"
            onClick={onSwitchToDefault}
            className="cursor-pointer"
          >
            {t('Switch to Default Team')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
